<template>
  <div class="vehicle-trade-detail">
    <div class="header">
      <el-button @click="goBack">返回列表</el-button>
      <h2>申请详情</h2>
    </div>
    
    <div v-loading="loading" class="detail-content">
      <el-card v-if="detailData" class="detail-card">
        <!-- 基本信息 -->
        <div slot="header" class="card-header">
          <span>基本信息</span>
          <el-tag v-if="detailData.status === 'pending'" type="warning">待处理</el-tag>
          <el-tag v-else-if="detailData.status === 'processing'" type="info">处理中</el-tag>
          <el-tag v-else-if="detailData.status === 'done'" type="success">已完成</el-tag>
          <el-tag v-else-if="detailData.status === 'failed'" type="danger">失败</el-tag>
        </div>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="任务ID">{{ detailData.id }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag v-if="detailData.status === 'pending'" type="warning">待处理</el-tag>
            <el-tag v-else-if="detailData.status === 'processing'" type="info">处理中</el-tag>
            <el-tag v-else-if="detailData.status === 'done'" type="success">已完成</el-tag>
            <el-tag v-else-if="detailData.status === 'failed'" type="danger">失败</el-tag>
            <span v-else>{{ detailData.status }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="重试次数">{{ detailData.retryCount }} / {{ detailData.maxRetries }}</el-descriptions-item>
          <el-descriptions-item label="关联ID" v-if="detailData.relatedId">{{ detailData.relatedId }}</el-descriptions-item>
          <el-descriptions-item label="创建人ID" v-if="detailData.createdBy">{{ detailData.createdBy }}</el-descriptions-item>
          <el-descriptions-item label="创建人姓名" v-if="detailData.creatorName || detailData.creatorUsername">
            <span v-if="detailData.creatorName">{{ detailData.creatorName }}</span>
            <span v-else-if="detailData.creatorUsername">{{ detailData.creatorUsername }}</span>
            <span v-if="detailData.creatorUsername && detailData.creatorName && detailData.creatorUsername !== detailData.creatorName" style="color: #999; font-size: 12px; margin-left: 8px;">
              ({{ detailData.creatorUsername }})
            </span>
          </el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ formatDate(detailData.createdAt) }}</el-descriptions-item>
          <el-descriptions-item label="更新时间">{{ formatDate(detailData.updatedAt) }}</el-descriptions-item>
          <el-descriptions-item label="领取时间" v-if="detailData.pickedAt">{{ formatDate(detailData.pickedAt) }}</el-descriptions-item>
          <el-descriptions-item label="完成时间" v-if="detailData.finishedAt">{{ formatDate(detailData.finishedAt) }}</el-descriptions-item>
          <el-descriptions-item label="备注" v-if="detailData.remark" :span="2">{{ detailData.remark }}</el-descriptions-item>
          <el-descriptions-item label="失败原因" v-if="detailData.lastError" :span="2">
            <span style="color: #f56c6c;">{{ detailData.lastError }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="扩展数据" v-if="detailData.payload" :span="2">
            <pre style="background: #f5f5f5; padding: 10px; border-radius: 4px; max-height: 200px; overflow: auto;">{{ formatPayload(detailData.payload) }}</pre>
          </el-descriptions-item>
        </el-descriptions>
      </el-card>
      
      <!-- 买方信息 -->
      <el-card v-if="detailData" class="detail-card">
        <div slot="header" class="card-header">
          <span>买方信息</span>
        </div>
        <el-row :gutter="20">
          <el-col :span="12">
            <div class="image-item">
              <div class="image-label">身份证正面</div>
              <el-image
                v-if="getImageUrl(detailData.buyerIdFront)"
                :src="getImageUrl(detailData.buyerIdFront)"
                :preview-src-list="[getImageUrl(detailData.buyerIdFront)]"
                fit="contain"
                style="width: 200px; height: 120px;">
              </el-image>
              <span v-else class="no-image">未上传</span>
            </div>
          </el-col>
          <el-col :span="12">
            <div class="image-item">
              <div class="image-label">身份证反面</div>
              <el-image
                v-if="getImageUrl(detailData.buyerIdBack)"
                :src="getImageUrl(detailData.buyerIdBack)"
                :preview-src-list="[getImageUrl(detailData.buyerIdBack)]"
                fit="contain"
                style="width: 200px; height: 120px;">
              </el-image>
              <span v-else class="no-image">未上传</span>
            </div>
          </el-col>
        </el-row>
        <el-descriptions :column="2" border style="margin-top: 20px;">
          <el-descriptions-item label="买方名称">{{ detailData.buyerName || '未填写' }}</el-descriptions-item>
          <el-descriptions-item label="身份证号码/单位代码">{{ detailData.buyerId || '未填写' }}</el-descriptions-item>
          <el-descriptions-item label="是否自然人">{{ detailData.buyerIsNaturalPerson ? '是' : '否' }}</el-descriptions-item>
          <el-descriptions-item label="买方地址">{{ detailData.buyerAddress || '未填写' }}</el-descriptions-item>
          <el-descriptions-item label="联系电话">{{ detailData.buyerPhone || '未填写' }}</el-descriptions-item>
        </el-descriptions>
      </el-card>
      
      <!-- 卖方信息 -->
      <el-card v-if="detailData" class="detail-card">
        <div slot="header" class="card-header">
          <span>卖方信息</span>
        </div>
        <el-row :gutter="20">
          <el-col :span="12">
            <div class="image-item">
              <div class="image-label">身份证正面</div>
              <el-image
                v-if="getImageUrl(detailData.sellerIdFront)"
                :src="getImageUrl(detailData.sellerIdFront)"
                :preview-src-list="[getImageUrl(detailData.sellerIdFront)]"
                fit="contain"
                style="width: 200px; height: 120px;">
              </el-image>
              <span v-else class="no-image">未上传</span>
            </div>
          </el-col>
          <el-col :span="12">
            <div class="image-item">
              <div class="image-label">身份证反面</div>
              <el-image
                v-if="getImageUrl(detailData.sellerIdBack)"
                :src="getImageUrl(detailData.sellerIdBack)"
                :preview-src-list="[getImageUrl(detailData.sellerIdBack)]"
                fit="contain"
                style="width: 200px; height: 120px;">
              </el-image>
              <span v-else class="no-image">未上传</span>
            </div>
          </el-col>
        </el-row>
        <el-descriptions :column="2" border style="margin-top: 20px;">
          <el-descriptions-item label="卖方名称">{{ detailData.sellerName || '未填写' }}</el-descriptions-item>
          <el-descriptions-item label="身份证号码/单位代码">{{ detailData.sellerId || '未填写' }}</el-descriptions-item>
          <el-descriptions-item label="是否自然人">{{ detailData.sellerIsNaturalPerson ? '是' : '否' }}</el-descriptions-item>
          <el-descriptions-item label="卖方地址">{{ detailData.sellerAddress || '未填写' }}</el-descriptions-item>
          <el-descriptions-item label="联系电话">{{ detailData.sellerPhone || '未填写' }}</el-descriptions-item>
        </el-descriptions>
      </el-card>
      
      <!-- 车辆信息 -->
      <el-card v-if="detailData" class="detail-card">
        <div slot="header" class="card-header">
          <span>车辆信息</span>
        </div>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="车辆类型搜索关键词">{{ detailData.vehicleTypeKeyword || '未填写' }}</el-descriptions-item>
          <el-descriptions-item label="车辆类型">{{ detailData.vehicleType || '未填写' }}</el-descriptions-item>
          <el-descriptions-item label="车牌号">{{ detailData.carPlate || '未填写' }}</el-descriptions-item>
          <el-descriptions-item label="登记证书号">{{ detailData.registerNo || '未填写' }}</el-descriptions-item>
          <el-descriptions-item label="厂牌型号">{{ detailData.brandModel || '未填写' }}</el-descriptions-item>
          <el-descriptions-item label="车架号/车辆识别代号">{{ detailData.vin || '未填写' }}</el-descriptions-item>
          <el-descriptions-item label="转入地车辆管理所名称">{{ detailData.transferOffice || '未填写' }}</el-descriptions-item>
          <el-descriptions-item label="车价合计（小写）">{{ detailData.priceTotal ? '¥' + detailData.priceTotal : '未填写' }}</el-descriptions-item>
          <el-descriptions-item label="备用字段1" v-if="detailData.ext1">{{ detailData.ext1 }}</el-descriptions-item>
          <el-descriptions-item label="备用字段2" v-if="detailData.ext2">{{ detailData.ext2 }}</el-descriptions-item>
          <el-descriptions-item label="备用字段3" v-if="detailData.ext3">{{ detailData.ext3 }}</el-descriptions-item>
        </el-descriptions>
      </el-card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VehicleTradeDetail',
  data() {
    return {
      loading: false,
      detailData: null
    }
  },
  mounted() {
    this.loadDetail()
  },
  methods: {
    loadDetail() {
      this.loading = true
      const id = this.$route.params.id
      this.$http.get(`/VehicleTrade/${id}`)
        .then(res => {
          this.loading = false
          if (res.data.success) {
            this.detailData = res.data.data
            // 调试：打印数据查看创建人信息
            console.log('详情数据:', this.detailData)
            console.log('创建人信息:', {
              createdBy: this.detailData.createdBy,
              creatorName: this.detailData.creatorName,
              creatorUsername: this.detailData.creatorUsername
            })
          } else {
            this.$message.error(res.data.message || '查询失败')
            this.goBack()
          }
        })
        .catch(err => {
          this.loading = false
          this.$message.error('网络错误，请重试')
          this.goBack()
        })
    },
    getImageUrl(imageData) {
      if (!imageData) return null
      try {
        // 如果是JSON字符串，解析获取URL
        const parsed = typeof imageData === 'string' ? JSON.parse(imageData) : imageData
        return parsed.url || imageData
      } catch (e) {
        // 如果不是JSON，直接返回
        return imageData
      }
    },
    goBack() {
      this.$router.push('/vehicle-trade/list')
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    formatPayload(payload) {
      if (!payload) return ''
      try {
        return JSON.stringify(payload, null, 2)
      } catch (e) {
        return String(payload)
      }
    }
  }
}
</script>

<style scoped>
.vehicle-trade-detail {
  padding: 20px;
}

.header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.header h2 {
  margin-left: 20px;
  font-size: 24px;
  color: #333;
}

.detail-content {
  background: #f5f5f5;
  min-height: calc(100vh - 100px);
}

.detail-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.image-item {
  margin-bottom: 20px;
}

.image-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
}

.no-image {
  color: #999;
  font-size: 14px;
}
</style>

